import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Check, Printer, RefreshCcw } from "lucide-react";

/**
 * Public, no-login view of a single shared To-Do list.
 *
 * Mounted at:
 *   /lists/:token         — interactive web view (recipients can tick items)
 *   /lists/:token/print   — paper-friendly view (auto-fires the print dialog)
 *
 * The token *is* the capability — anyone holding it can view + check items.
 * Family members never appear here; we deliberately don't surface family_id
 * or any other workspace metadata from the public endpoint.
 */
export default function TodoSharePage() {
  const { token } = useParams();
  const printMode =
    typeof window !== "undefined" &&
    window.location.pathname.endsWith("/print");

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const d = await api.todoShareView(token);
      setData(d);
      setError(null);
    } catch (err) {
      const status = err?.response?.status;
      setError(
        status === 404
          ? "This link no longer works. The family may have rotated it."
          : "Could not load this list."
      );
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  // In print mode, fire the OS print dialog as soon as the list paints.
  useEffect(() => {
    if (!printMode) return;
    if (!data) return;
    const t = setTimeout(() => {
      try {
        window.print();
      } catch {}
    }, 350);
    return () => clearTimeout(t);
  }, [printMode, data]);

  const toggle = async (item) => {
    if (printMode) return;
    if (busyId) return;
    setBusyId(item.id);
    const next = !item.done;
    setData((d) => ({
      ...d,
      items: d.items.map((i) => (i.id === item.id ? { ...i, done: next } : i)),
    }));
    try {
      await api.todoShareCheck(token, item.id, next);
    } catch {
      // Roll back; re-load to be sure we're in sync with the family.
      setData((d) => ({
        ...d,
        items: d.items.map((i) => (i.id === item.id ? { ...i, done: !next } : i)),
      }));
      load();
    } finally {
      setBusyId(null);
    }
  };

  if (error) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-6 bg-[color:var(--bg)]"
        data-testid="todo-share-error"
      >
        <div className="max-w-sm text-center space-y-3">
          <div className="font-display-italic text-3xl">Link expired.</div>
          <p className="text-sm text-[color:var(--ink-3)] leading-relaxed">{error}</p>
          <button
            onClick={load}
            className="ff-btn inline-flex items-center gap-1"
            data-testid="todo-share-retry"
          >
            <RefreshCcw className="w-4 h-4" strokeWidth={2} /> Try again
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[color:var(--bg)]">
        <div className="font-display text-2xl text-[color:var(--ink-3)]">Loading…</div>
      </div>
    );
  }

  const total = data.items.length;
  const remaining = data.items.filter((i) => !i.done).length;

  return (
    <div
      className={`min-h-screen bg-[color:var(--bg)] ${
        printMode ? "todo-print-mode" : ""
      }`}
      data-testid="todo-share-page"
    >
      <div className="max-w-2xl mx-auto px-5 md:px-8 py-8 md:py-14">
        <div className="mb-6 print:mb-4">
          <div className="label-tiny">Shared list</div>
          <h1
            className="font-display-italic text-4xl md:text-5xl leading-none mt-1 tracking-tight"
            data-testid="todo-share-title"
          >
            {data.title}
          </h1>
          <div className="text-[13px] text-[color:var(--ink-3)] mt-2">
            {total === 0
              ? "Nothing on this list yet."
              : remaining === 0
              ? `All ${total} done ✨`
              : `${remaining} of ${total} left`}
          </div>
        </div>

        {!printMode && (
          <div className="mb-5 print:hidden">
            <a
              href={`/lists/${token}/print`}
              target="_blank"
              rel="noopener noreferrer"
              className="ff-btn inline-flex items-center gap-1"
              data-testid="todo-share-open-print"
            >
              <Printer className="w-3.5 h-3.5" strokeWidth={2} /> Print this list
            </a>
          </div>
        )}

        {data.items.length === 0 ? (
          <div className="text-[color:var(--ink-3)] text-sm">
            Whoever shared this list with you hasn't added anything yet.
          </div>
        ) : (
          <ul className="space-y-2" data-testid="todo-share-items">
            {data.items.map((item) => (
              <li
                key={item.id}
                className={`ff-card p-3 flex items-center gap-3 ${
                  item.done ? "opacity-60" : ""
                }`}
                data-testid={`todo-share-row-${item.id}`}
              >
                <button
                  type="button"
                  onClick={() => toggle(item)}
                  disabled={printMode}
                  className={`w-9 h-9 shrink-0 rounded-full border flex items-center justify-center transition-colors ${
                    item.done
                      ? "bg-[#CDE6D2] border-[#7db389]"
                      : "border-[color:var(--line)] hover:bg-[color:var(--bg-soft)]"
                  } ${printMode ? "cursor-default" : ""}`}
                  aria-label={item.done ? "Mark not done" : "Mark done"}
                  data-testid={`todo-share-toggle-${item.id}`}
                >
                  {item.done ? <Check className="w-4 h-4" strokeWidth={2.5} /> : null}
                </button>
                <div className="flex-1 min-w-0">
                  <div
                    className={`text-[16px] leading-snug ${
                      item.done
                        ? "line-through text-[color:var(--ink-2)]"
                        : "text-[color:var(--ink)]"
                    }`}
                  >
                    {item.text}
                  </div>
                  {item.due_date ? (() => {
                    // Mirror the family-side row's due-date pill so the
                    // recipient knows which items the family flagged as
                    // time-sensitive. Only colour-codes today/overdue —
                    // future dates render in the neutral pill colour.
                    const t = new Date().toISOString().slice(0, 10);
                    const tomorrow = (() => {
                      const d = new Date();
                      d.setDate(d.getDate() + 1);
                      return d.toISOString().slice(0, 10);
                    })();
                    const isToday = item.due_date === t;
                    const isTomorrow = item.due_date === tomorrow;
                    const isOverdue = item.due_date < t;
                    const tone = isOverdue
                      ? "bg-[#F6D6DA] text-[#7A2E3A]"
                      : isToday
                      ? "bg-[#FFE2C7] text-[#7A4A1F]"
                      : "bg-[color:var(--bg-soft)]";
                    const label = isOverdue
                      ? `Overdue · ${item.due_date}`
                      : isToday
                      ? "Due today"
                      : isTomorrow
                      ? "Due tomorrow"
                      : `Due ${item.due_date}`;
                    return (
                      <div
                        className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[11px] ${tone}`}
                        data-testid={`todo-share-due-${item.id}`}
                      >
                        {label}
                      </div>
                    );
                  })() : null}
                </div>
              </li>
            ))}
          </ul>
        )}

        <p className="mt-10 text-[11px] text-[color:var(--ink-3)] leading-relaxed print:mt-6">
          Shared with you from a FamFeel family list. You can tick items off but
          can't add, rename, or delete them.
        </p>
      </div>

      {/* Print-only tweaks: cleaner paper layout + hide interactive bits. */}
      <style>{`
        @media print {
          body { background: #fff !important; }
          .ff-btn, .print\\:hidden { display: none !important; }
          .ff-card {
            box-shadow: none !important;
            border: 1px solid rgba(0, 0, 0, 0.18) !important;
            page-break-inside: avoid;
          }
        }
      `}</style>
    </div>
  );
}
